import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ExternalLink, Eye, FileText, Loader2 } from 'lucide-react'
import {
  Button,
  Input,
  Modal,
  PageHeaderBand,
  QueryErrorState,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  toast,
} from '@/components/ui'
import { useAutosaveForm } from '@/shell/forms/autosave'
import {
  FormField,
  PanelCard,
  PdfDropZone,
  SaveStatusPill,
  SectionNav,
  StepSection,
  type SectionStatus,
} from '@/shell/forms/FormKit'
import { jumpTo } from '@/shell/forms/jump'
import {
  isReadingPdf,
  usePublishSop,
  useReadSopPdf,
  useReplaceSopPdf,
  useSop,
  useSopCategories,
  useSopTemplate,
  useUpdateSop,
  type UpdateSopVars,
} from '../api/queries'
import type { SopDetail, SopExtractionReport, SopHeaderKey, SopSectionKey, SopStatus } from '../api/types'
import { sopDownloadUrl } from '../utils/sop-url'
import { SopSectionEditor } from './SopSectionEditor'
import { SopStatusChip } from './SopStatusChip'

const SECTION_KEYS: SopSectionKey[] = ['purpose', 'scope', 'definitions', 'policy', 'procedure', 'controls', 'references']

type SectionField = `section_${SopSectionKey}`

type SopForm = {
  title: string
  category: string
  tags: string
} & Record<SopHeaderKey, string> & Record<SectionField, string>

function toForm(sop: SopDetail): SopForm {
  const form = {
    title: sop.title,
    category: sop.category,
    tags: sop.tags.join(', '),
    department: sop.department ?? '',
    owner: sop.owner ?? '',
    version: sop.version ?? '',
    effective_date: sop.effective_date ?? '',
    approved_by: sop.approved_by ?? '',
    next_review_date: sop.next_review_date ?? '',
  } as SopForm
  for (const key of SECTION_KEYS) form[`section_${key}`] = sop.sections?.[key] ?? ''
  return form
}

function toPatch(id: string, values: Partial<SopForm>): UpdateSopVars {
  const patch: UpdateSopVars = { id }
  const sections: Partial<Record<SopSectionKey, string>> = {}
  for (const [k, v] of Object.entries(values) as [keyof SopForm, string][]) {
    if (k === 'title') patch.title = v
    else if (k === 'category') patch.category = v
    else if (k === 'tags') patch.tags = v.split(',').map((t) => t.trim()).filter(Boolean)
    else if (k.startsWith('section_')) sections[k.slice('section_'.length) as SopSectionKey] = v
    else patch[k as SopHeaderKey] = v.trim() || null
  }
  if (Object.keys(sections).length) patch.sections = sections
  return patch
}

const HEADER_REQUIRED: SopHeaderKey[] = ['department', 'owner', 'version', 'effective_date', 'approved_by']

export interface SopEditorProps {
  sopId: string
  /** The reader page for this SOP (and where "back" goes). */
  viewTo: string
}

/** Full-page editor for one SOP, laid out as the SPI SOP template. */
export function SopEditor({ sopId, viewTo }: SopEditorProps) {
  const { data: sop, isError, error, refetch } = useSop(sopId)
  if (isError) {
    return (
      <div className="p-6">
        <QueryErrorState error={error} onRetry={() => void refetch()} subject="this SOP" />
      </div>
    )
  }
  if (!sop) return <div className="p-6 text-md text-secondary">Loading…</div>
  // Keyed by id only: a refetch must never remount the form (autosave rule).
  return <SopEditorForm key={sop.id} sop={sop} viewTo={viewTo} />
}

function SopEditorForm({ sop, viewTo }: { sop: SopDetail; viewTo: string }) {
  const navigate = useNavigate()
  const { data: template } = useSopTemplate()
  const { data: categoriesData } = useSopCategories()
  const categories = categoriesData?.categories ?? []
  const update = useUpdateSop()
  const replacePdf = useReplaceSopPdf()
  const readPdf = useReadSopPdf()
  const publish = usePublishSop()
  const [confirmReplace, setConfirmReplace] = useState(false)

  const server = useMemo(() => toForm(sop), [sop])
  const save = useCallback(
    (values: Partial<SopForm>) => update.mutateAsync(toPatch(sop.id, values)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutateAsync is stable
    [sop.id],
  )
  const { form, set, status, error: saveError, flush } = useAutosaveForm(server, save)

  const templateSections = template?.sections ?? []
  const isPdf = sop.has_pdf
  // A PDF uploaded before SOP PDFs were read into the template.
  const isLegacyPdf = isPdf && !sop.is_template
  const isFreeForm = !isPdf && !sop.is_template
  const reading = isReadingPdf(sop)
  const sectionStep = isPdf ? 4 : 3

  const startRead = (replace: boolean) =>
    readPdf.mutate(
      { id: sop.id, replace },
      { onError: (err) => toast.danger(`Couldn't start reading the PDF${err instanceof Error ? `: ${err.message}` : ''}`) },
    )

  const headerMissing = HEADER_REQUIRED.filter((k) => !form[k].trim()).length
  const sectionStatus = (key: SopSectionKey, required: boolean): SectionStatus | null => {
    const filled = !isEmptyHtml(form[`section_${key}`])
    if (filled) return { missing: 0, warnings: 0, complete: true }
    return required ? { missing: 1, warnings: 0, complete: false } : null
  }

  const nav = [
    { id: 'details', step: 1, title: 'Details', status: form.title.trim() ? { missing: 0, warnings: 0, complete: true } : { missing: 1, warnings: 0, complete: false } },
    { id: 'control', step: 2, title: 'Document control', status: { missing: headerMissing, warnings: 0, complete: headerMissing === 0 } },
    ...(isPdf ? [{ id: 'pdf', step: 3, title: 'Attached PDF', status: null }] : []),
    ...(sop.is_template
      ? templateSections.map((s, i) => ({ id: s.key, step: i + sectionStep, title: s.label, status: sectionStatus(s.key, s.required) }))
      : []),
  ]

  const leave = async () => {
    await flush()
    navigate({ to: viewTo })
  }

  return (
    <div data-density="comfortable" data-surface="form" className="flex h-full flex-col">
      <PageHeaderBand title={form.title || 'Untitled SOP'} description="Every SOP follows the SPI SOP template. Changes save automatically.">
        <div className="ml-auto flex items-center gap-2">
          <SopStatusChip status={sop.status as SopStatus} />
        </div>
      </PageHeaderBand>

      <Toolbar>
        <ToolbarLeft>
          <Button variant="ghost" size="sm" onClick={() => void leave()}>
            <ArrowLeft size={16} /> Back to SOP
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          <SaveStatusPill status={status} error={saveError} />
          <Button variant="secondary" size="sm" onClick={() => void leave()}>
            <Eye size={16} /> View
          </Button>
          {sop.status !== 'published' ? (
            <Button
              variant="primary"
              size="sm"
              loading={publish.isPending}
              onClick={async () => {
                await flush()
                publish.mutate(sop.id, {
                  onSuccess: () => {
                    toast.success('SOP published')
                    navigate({ to: viewTo })
                  },
                  onError: (err) => toast.danger(`Publish failed${err instanceof Error ? `: ${err.message}` : ''}`),
                })
              }}
            >
              Publish
            </Button>
          ) : null}
        </ToolbarRight>
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-auto bg-canvas px-6 py-6">
        <div className="mx-auto flex w-full max-w-[calc(var(--form-content-width)+var(--form-aside-width)+1.5rem)] items-start gap-6">
          <div className="flex min-w-0 flex-1 flex-col gap-6">
            {reading ? (
              <div role="status" className="flex items-center gap-3 rounded-lg border border-info-border bg-info-bg px-5 py-4 text-md text-info-text">
                <Loader2 size={20} className="animate-spin" aria-hidden="true" />
                <span>
                  <strong className="font-semibold">Reading the PDF into the template…</strong> This takes about a minute. Sections
                  and document-control fields fill in on their own; anything you type is kept.
                </span>
              </div>
            ) : null}
            {sop.extraction_state === 'failed' ? (
              <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-danger-border bg-danger-bg px-5 py-4 text-md text-danger-text">
                <span className="flex-1">
                  <strong className="font-semibold">The PDF couldn't be read.</strong> {sop.extraction_error}
                </span>
                <Button variant="secondary" loading={readPdf.isPending} onClick={() => startRead(false)}>
                  Try again
                </Button>
              </div>
            ) : null}

            <StepSection id="details" step={1} title="Details" description="What the SOP is called and where it is filed.">
              <FormField id="title" label="Title" required help='Descriptive and action-oriented, e.g. "Inventory Adjustment and PO Discrepancy SOP".'>
                <Input value={form.title} onChange={(e) => set('title', e.target.value)} />
              </FormField>
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField id="category" label="Category" required>
                  <Select value={form.category} onChange={(e) => set('category', e.target.value)}>
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Select>
                </FormField>
                <FormField id="tags" label="Tags" help="Comma-separated, e.g. receiving, inspection.">
                  <Input value={form.tags} onChange={(e) => set('tags', e.target.value)} />
                </FormField>
              </div>
            </StepSection>

            <StepSection id="control" step={2} title="Document control" description="Shown at the top of the SOP. Required before an audit."
              status={nav[1]!.status}>
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField id="department" label="Department" required help="Departments that follow this SOP.">
                  <Input value={form.department} onChange={(e) => set('department', e.target.value)} />
                </FormField>
                <FormField id="owner" label="Owner" required help="Who keeps it current.">
                  <Input value={form.owner} onChange={(e) => set('owner', e.target.value)} />
                </FormField>
                <FormField id="version" label="Version" required help="1.0 to start; major changes 2.0, minor edits 1.1.">
                  <Input value={form.version} onChange={(e) => set('version', e.target.value)} />
                </FormField>
                <FormField id="approved_by" label="Approved by" required>
                  <Input value={form.approved_by} onChange={(e) => set('approved_by', e.target.value)} />
                </FormField>
                <FormField id="effective_date" label="Effective date" required help="Change only when a new version is approved.">
                  <Input type="date" value={form.effective_date} onChange={(e) => set('effective_date', e.target.value)} />
                </FormField>
                <FormField id="next_review_date" label="Next review" help="Review annually or when the Odoo process changes.">
                  <Input type="date" value={form.next_review_date} onChange={(e) => set('next_review_date', e.target.value)} />
                </FormField>
              </div>
            </StepSection>

            {isPdf ? (
              <StepSection id="pdf" step={3} title="Attached PDF"
                description="The uploaded PDF stays attached for reference. Its content is read into the sections below.">
                <div className="flex flex-wrap items-center gap-3">
                  <FileText size={20} className="text-tertiary" aria-hidden="true" />
                  <span className="flex-1 text-md text-primary">Original PDF</span>
                  <Button variant="ghost" onClick={() => window.open(sopDownloadUrl(sop.id), '_blank', 'noopener')}>
                    <ExternalLink size={16} /> Open
                  </Button>
                </div>
                {isLegacyPdf ? (
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-warning-border bg-warning-bg px-5 py-4 text-md text-warning-text">
                    <span className="flex-1">This PDF was uploaded before PDFs were read into the template. Read it now to fill in the sections.</span>
                    <Button variant="primary" loading={readPdf.isPending} onClick={() => startRead(false)}>
                      Read this PDF into the template
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" disabled={reading} loading={readPdf.isPending} onClick={() => startRead(false)}>
                      Read into empty sections
                    </Button>
                    <Button variant="ghost" disabled={reading} onClick={() => setConfirmReplace(true)}>
                      Replace sections with the PDF
                    </Button>
                  </div>
                )}
                <PdfDropZone
                  label="Replace PDF"
                  busy={replacePdf.isPending}
                  disabled={reading}
                  hint="Attach a newer version. If the sections are still empty it is read in automatically; otherwise use the buttons above."
                  onFile={(file) =>
                    replacePdf.mutate(
                      { id: sop.id, file },
                      {
                        onSuccess: () => toast.success('PDF replaced'),
                        onError: (err) => toast.danger(`Replace failed${err instanceof Error ? `: ${err.message}` : ''}`),
                      },
                    )
                  }
                />
              </StepSection>
            ) : null}

            {isFreeForm ? (
              <div role="status" className="flex flex-wrap items-center gap-3 rounded-lg border border-warning-border bg-warning-bg px-5 py-4 text-md text-warning-text">
                <span className="flex-1">This SOP was written before the template. Move its text into the template, then sort it into sections.</span>
                <Button variant="secondary" onClick={() => update.mutate({ id: sop.id, sections: { procedure: sop.body_html ?? '' } })}>
                  Move into template
                </Button>
              </div>
            ) : null}
            {sop.is_template
              ? templateSections.map((s, i) => (
                  <StepSection key={s.key} id={s.key} step={i + sectionStep} title={s.label} description={s.guidance}
                    status={sectionStatus(s.key, s.required)}>
                    {!s.required ? (
                      <p className="-mt-1 text-sm text-secondary">Optional. Leave empty if it doesn't apply; empty sections are hidden from readers.</p>
                    ) : null}
                    <SopSectionEditor
                      label={s.label}
                      value={form[`section_${s.key}`]}
                      onChange={(html) => set(`section_${s.key}`, html)}
                    />
                  </StepSection>
                ))
              : null}
          </div>

          <aside className="sticky top-0 hidden max-h-[calc(100vh-14rem)] w-[var(--form-aside-width)] shrink-0 flex-col gap-4 overflow-auto xl:flex">
            <PanelCard title="On this page">
              <SectionNav sections={nav} onJump={(id) => jumpTo(`section-${id}`)} />
            </PanelCard>
            {sop.extraction_report ? <PdfCheckCard report={sop.extraction_report} /> : null}
            <PanelCard title={sop.gaps.length ? `Template: ${sop.gaps.length} to complete` : 'Template: complete'}>
              {sop.gaps.length ? (
                <ul className="flex flex-col gap-1.5 text-md">
                  {sop.gaps.map((g) => (
                    <li key={g} className="text-warning-text">{g}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-md text-secondary">Every required field and section is filled in.</p>
              )}
              <p className="text-sm text-secondary">Required: Purpose, Scope, Procedure and the document-control fields. Other sections are optional.</p>
            </PanelCard>
          </aside>
        </div>
      </div>

      <Modal
        open={confirmReplace}
        onOpenChange={setConfirmReplace}
        surface="form"
        tone="warning"
        title="Replace the sections with what the PDF says?"
        description="Every section that the PDF has content for is overwritten, including text you wrote. The current version is kept in the SOP's history."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmReplace(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={async () => {
                setConfirmReplace(false)
                await flush()
                startRead(true)
              }}
            >
              Replace sections
            </Button>
          </>
        }
      />
    </div>
  )
}

/** How well the read matches the PDF's own text (wording check). */
function PdfCheckCard({ report }: { report: SopExtractionReport }) {
  if (!report.checked) {
    return (
      <PanelCard title="Check against the PDF">
        <p className="text-md text-secondary">{report.reason ?? 'The PDF has no text to compare against.'}</p>
        {report.notes ? <p className="text-sm text-secondary">{report.notes}</p> : null}
      </PanelCard>
    )
  }
  const pct = `${Math.round((report.coverage ?? 0) * 1000) / 10}%`
  return (
    <PanelCard title={report.ok ? `Matches the PDF: ${pct}` : `Check against the PDF: ${pct} matched`}>
      {report.ok ? (
        <p className="text-md text-secondary">The sections use the PDF's own wording.</p>
      ) : (
        <div className="flex flex-col gap-1.5 text-md text-warning-text">
          {report.missing_sample?.length ? <p>Words in the PDF but not in the SOP: {report.missing_sample.join(', ')}</p> : null}
          {report.added_sample?.length ? <p>Words in the SOP but not in the PDF: {report.added_sample.join(', ')}</p> : null}
        </div>
      )}
      {report.notes ? <p className="text-sm text-secondary">Note from the reader: {report.notes}</p> : null}
    </PanelCard>
  )
}

function isEmptyHtml(html: string): boolean {
  if (!html) return true
  if (/<(img|table)\b/i.test(html)) return false
  return !html.replace(/<[^>]+>/g, '').replace(/&nbsp;|\u00a0/g, ' ').trim()
}
